import posthog from 'posthog-js';

/**
 * Reads the current PostHog identifiers from the browser. These are attached to
 * the Stripe Checkout session so the backend can merge the anonymous journey
 * with the identified customer even if they never return to the success page.
 */
export function getPosthogIdentity(): {
  distinctId: string | null;
  sessionId: string | null;
} {
  try {
    return {
      distinctId: posthog.get_distinct_id?.() ?? null,
      sessionId: posthog.get_session_id?.() ?? null,
    };
  } catch {
    return { distinctId: null, sessionId: null };
  }
}

/**
 * Links the current anonymous browser session to a known customer (by email).
 * PostHog merges the anonymous distinct_id's full history into this identity,
 * so the pre-checkout journey and the server-side `order_completed` event line
 * up under one person.
 */
export function identifyCustomer(
  email: string,
  properties?: Record<string, unknown>
) {
  if (!email) return;
  posthog.identify(email, properties);
}

interface ProductInfo {
  id: string;
  name: string;
  price: number;
  currency: string;
}

interface VariantInfo {
  size: string;
  price: number;
}

export function trackProductListViewed(products: ProductInfo[]) {
  posthog.capture('product_list_viewed', {
    products: products.map((p) => ({
      product_id: p.id,
      name: p.name,
      price: p.price,
      currency: p.currency,
    })),
    product_count: products.length,
  });
}

export function trackProductCardClicked(
  product: { id: string; name: string; price: number },
  currency: string,
  ctaVariant: string | boolean | undefined
) {
  posthog.capture('product_card_clicked', {
    product_id: product.id,
    product_name: product.name,
    price: product.price,
    currency,
    cta_variant: ctaVariant ?? 'unknown',
    cta_shown: ctaVariant === 'test',
  });
}

export function trackProductViewed(
  product: { id: string; name: string; category?: string },
  variant: VariantInfo,
  currency: string
) {
  posthog.capture('product_viewed', {
    product_id: product.id,
    product_name: product.name,
    category: product.category,
    variant: variant.size,
    price: variant.price,
    currency,
  });
}

export function trackProductAdded(
  product: { id: string; name: string },
  variant: VariantInfo,
  quantity: number,
  currency: string
) {
  posthog.capture('product_added', {
    product_id: product.id,
    product_name: product.name,
    variant: variant.size,
    price: variant.price,
    quantity,
    currency,
  });
}

export function trackCartViewed(items: { productId: string; name: string; quantity: number; price: number }[], currency: string) {
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  posthog.capture('cart_viewed', {
    item_count: items.length,
    cart_total: total,
    currency,
    items: items.map((i) => ({
      product_id: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: i.price,
    })),
  });
}

export function trackCheckoutStarted(
  items: { productId: string; name: string; quantity: number; price: number }[],
  currency: string
) {
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  posthog.capture('checkout_started', {
    item_count: items.length,
    cart_total: total,
    currency,
    items: items.map((i) => ({
      product_id: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: i.price,
    })),
  });
}

export function trackCheckoutButtonClicked(
  items: { productId: string; name: string; quantity: number; price: number }[],
  currency: string
) {
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  posthog.capture('checkout_button_clicked', {
    item_count: items.length,
    cart_total: total,
    currency,
    items: items.map((i) => ({
      product_id: i.productId,
      name: i.name,
      quantity: i.quantity,
      price: i.price,
    })),
  });
}

export function trackCheckoutRedirectedToStripe(
  currency: string,
  itemCount: number
) {
  posthog.capture('checkout_redirected_to_stripe', {
    currency,
    item_count: itemCount,
  });
}

export function trackOrderCompleted(sessionId: string | null, total: number, currency: string, itemCount: number) {
  posthog.capture('order_completed', {
    session_id: sessionId,
    total,
    currency,
    item_count: itemCount,
  });
}

export function trackSubscriptionStarted(
  product: { id: string; name: string },
  size: string,
  currency: string
) {
  posthog.capture('subscription_started', {
    product_id: product.id,
    product_name: product.name,
    variant: size,
    currency,
  });
}

export function trackSubscriptionCheckoutButtonClicked(
  product: { id: string; name: string },
  variant: { size: string; price: number },
  currency: string
) {
  posthog.capture('subscription_checkout_button_clicked', {
    product_id: product.id,
    product_name: product.name,
    variant: variant.size,
    price: variant.price,
    currency,
  });
}

export function trackSubscriptionRedirectedToStripe(
  product: { id: string; name: string },
  variant: { size: string; price: number },
  currency: string
) {
  posthog.capture('subscription_redirected_to_stripe', {
    product_id: product.id,
    product_name: product.name,
    variant: variant.size,
    price: variant.price,
    currency,
  });
}
